import { Vector2 } from "../../maths/Vector2";
const minAngle = 15 * Math.PI / 180;
const precision = 1e-13;
const tempData = new Array(256);
const tempIndexs = new Array(4);
const vec2 = new Vector2();
export class BasePoly {
    static _checkMinAngle(p1x, p1y, p2x, p2y, p3x, p3y) {
        const v1x = p2x - p1x;
        const v1y = p2y - p1y;
        const v2x = p3x - p2x;
        const v2y = p3y - p2y;
        const dot = v1x * v2x + v1y * v2y;
        const len1 = Math.sqrt(v1x * v1x + v1y * v1y);
        const len2 = Math.sqrt(v2x * v2x + v2y * v2y);
        const cosAngle = dot / (len1 * len2);
        const angle = Math.acos(Math.abs(cosAngle));
        return Math.abs(angle) < minAngle;
    }
    static createLine2(p, indices, lineWidth, indexBase, outVertex, loop) {
        if (p.length < 4)
            return null;
        let offset = indexBase;
        var points = tempData.length > (p.length + 2) ? tempData : new Array(p.length + 2);
        points[0] = p[0];
        points[1] = p[1];
        var newlen = 2;
        var i = 0;
        var length = p.length;
        for (i = 2; i < length; i += 2) {
            if (Math.abs(p[i] - p[i - 2]) + Math.abs(p[i + 1] - p[i - 1]) > 0.01) {
                points[newlen++] = p[i];
                points[newlen++] = p[i + 1];
            }
        }
        let delta = Math.abs(p[0] - points[newlen - 2]) + Math.abs(p[1] - points[newlen - 1]);
        if (loop && delta > 0) {
            if (delta > precision) {
                points[newlen++] = p[0];
                points[newlen++] = p[1];
            }
            else {
                points[newlen - 2] = p[0];
                points[newlen - 1] = p[1];
            }
        }
        var result = outVertex;
        length = newlen / 2;
        var w = lineWidth / 2;
        var p1x, p1y, p2x, p2y, p3x, p3y;
        p1x = points[0];
        p1y = points[1];
        p2x = points[2];
        p2y = points[3];
        this.getNormal(p1x, p1y, p2x, p2y, w, vec2);
        result.push(p1x - vec2.x, p1y - vec2.y, p1x + vec2.x, p1y + vec2.y);
        for (i = 1; i < length - 1; i++) {
            p1x = points[(i - 1) * 2];
            p1y = points[(i - 1) * 2 + 1];
            p2x = points[(i) * 2];
            p2y = points[(i) * 2 + 1];
            p3x = points[(i + 1) * 2];
            p3y = points[(i + 1) * 2 + 1];
            indices.push(indexBase + 0, indexBase + 1, indexBase + 3, indexBase + 3, indexBase + 2, indexBase + 0);
            indexBase += 2;
            indexBase += this._setMiddleVertexs(p1x, p1y, p2x, p2y, p3x, p3y, w, result, vec2, indices, indexBase);
        }
        p1x = points[newlen - 4];
        p1y = points[newlen - 3];
        p2x = points[newlen - 2];
        p2y = points[newlen - 1];
        this.getNormal(p1x, p1y, p2x, p2y, w, vec2);
        result.push(p2x - vec2.x, p2y - vec2.y, p2x + vec2.x, p2y + vec2.y);
        indices.push(indexBase + 0, indexBase + 1, indexBase + 3, indexBase + 3, indexBase + 2, indexBase + 0);
        if (p2x == points[0] && p2y == points[1]) {
            p3x = points[2];
            p3y = points[3];
            let last = result.length / 2;
            indexBase += 4;
            tempIndexs[0] = offset + last - 2;
            tempIndexs[1] = offset + last - 1;
            tempIndexs[2] = offset;
            tempIndexs[3] = offset + 1;
            this._setMiddleVertexs(p1x, p1y, p2x, p2y, p3x, p3y, w, result, vec2, indices, indexBase, tempIndexs);
        }
        return result;
    }
    static _setMiddleVertexs(x1, y1, x2, y2, x3, y3, w, vertexs, out, indices, indexBase, edgeIndexArray = null) {
        this.getNormal(x1, y1, x2, y2, w, out);
        let perpx = out.x;
        let perpy = out.y;
        this.getNormal(x2, y2, x3, y3, w, out);
        let perp2x = out.x;
        let perp2y = out.y;
        if (this._checkMinAngle(x1, y1, x2, y2, x3, y3)) {
            if (!edgeIndexArray) {
                vertexs.push(x2 - perpx, y2 - perpy, x2 + perpx, y2 + perpy);
                vertexs.push(x2 - perp2x, y2 - perp2y, x2 + perp2x, y2 + perp2y);
                indices.push(indexBase + 0, indexBase + 1, indexBase + 3, indexBase + 3, indexBase + 2, indexBase + 0);
            }
            else {
                indices.push(edgeIndexArray[0], edgeIndexArray[1], edgeIndexArray[3], edgeIndexArray[3], edgeIndexArray[2], edgeIndexArray[0]);
            }
            return 2;
        }
        let a1 = (-perpy + y1) - (-perpy + y2);
        let b1 = (-perpx + x2) - (-perpx + x1);
        let c1 = (-perpx + x1) * (-perpy + y2) - (-perpx + x2) * (-perpy + y1);
        let a2 = (-perp2y + y3) - (-perp2y + y2);
        let b2 = (-perp2x + x2) - (-perp2x + x3);
        let c2 = (-perp2x + x3) * (-perp2y + y2) - (-perp2x + x2) * (-perp2y + y3);
        let denom = a1 * b2 - a2 * b1;
        denom = a1 * b2 - a2 * b1;
        if (Math.abs(denom) < 0.1) {
            denom += 10.1;
            vertexs.push(x2 - perpx, y2 - perpy, x2 + perpx, y2 + perpy);
            return 0;
        }
        let px = (b1 * c2 - b2 * c1) / denom;
        let py = (a2 * c1 - a1 * c2) / denom;
        if (!edgeIndexArray) {
            vertexs.push(x2 - perpx, y2 - perpy, x2 + perpx, y2 + perpy);
            if (denom > 0) {
                vertexs.push(px, py, x2, y2);
                indices.push(indexBase + 0, indexBase + 2, indexBase + 4);
                indices.push(indexBase + 4, indexBase + 3, indexBase + 0);
            }
            else {
                vertexs.push(x2 - (px - x2), y2 - (py - y2), x2, y2);
                indices.push(indexBase + 1, indexBase + 2, indexBase + 5);
                indices.push(indexBase + 5, indexBase + 3, indexBase + 1);
            }
            vertexs.push(x2 - perp2x, y2 - perp2y, x2 + perp2x, y2 + perp2y);
        }
        else {
            if (denom > 0) {
                vertexs.push(px, py, x2, y2);
                indices.push(edgeIndexArray[0], indexBase + 0, edgeIndexArray[2]);
                indices.push(edgeIndexArray[2], indexBase + 1, edgeIndexArray[0]);
            }
            else {
                vertexs.push(x2 - (px - x2), y2 - (py - y2), x2, y2);
                indices.push(edgeIndexArray[1], indexBase + 0, edgeIndexArray[3]);
                indices.push(edgeIndexArray[3], indexBase + 1, edgeIndexArray[1]);
            }
        }
        return 4;
    }
    static getNormal(x1, y1, x2, y2, w, out) {
        if (!out) {
            out = new Vector2();
        }
        let perpx = y2 - y1;
        let perpy = x1 - x2;
        let dist = Math.sqrt(perpx * perpx + perpy * perpy);
        out.x = perpx / dist * w;
        out.y = perpy / dist * w;
        return out;
    }
    static createLineTriangle(path, color, width, loop, outvb, vbstride, outib) {
        var points = path.slice();
        var ptlen = points.length;
        var p1x = points[0], p1y = points[1];
        var p2x = points[2], p2y = points[2];
        var len = 0;
        var rp = 0;
        var dx = 0, dy = 0;
        var pointnum = ptlen / 2;
        if (pointnum <= 1)
            return;
        if (pointnum == 2) {
            return;
        }
        var tmpData = new Array(pointnum * 4);
        var realPtNum = 0;
        var ci = 0;
        for (var i = 0; i < pointnum - 1; i++) {
            p1x = points[ci++], p1y = points[ci++];
            p2x = points[ci++], p2y = points[ci++];
            dx = p2x - p1x, dy = p2y - p1y;
            if (dx != 0 && dy != 0) {
                len = Math.sqrt(dx * dx + dy * dy);
                if (len > 1e-3) {
                    rp = realPtNum * 4;
                    tmpData[rp] = p1x;
                    tmpData[rp + 1] = p1y;
                    tmpData[rp + 2] = dx / len;
                    tmpData[rp + 3] = dy / len;
                    realPtNum++;
                }
            }
        }
        if (loop) {
            p1x = points[ptlen - 2], p1y = points[ptlen - 1];
            p2x = points[0], p2y = points[1];
            dx = p2x - p1x, dy = p2y - p1y;
            if (dx != 0 && dy != 0) {
                len = Math.sqrt(dx * dx + dy * dy);
                if (len > 1e-3) {
                    rp = realPtNum * 4;
                    tmpData[rp] = p1x;
                    tmpData[rp + 1] = p1y;
                    tmpData[rp + 2] = dx / len;
                    tmpData[rp + 3] = dy / len;
                    realPtNum++;
                }
            }
        }
        else {
            rp = realPtNum * 4;
            tmpData[rp] = p1x;
            tmpData[rp + 1] = p1y;
            tmpData[rp + 2] = dx / len;
            tmpData[rp + 3] = dy / len;
            realPtNum++;
        }
        ci = 0;
        for (i = 0; i < pointnum; i++) {
            p1x = points[ci], p1y = points[ci + 1];
            p2x = points[ci + 2], p2y = points[ci + 3];
        }
        if (loop) {
        }
    }
}
